import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { useId } from "../lib/uid.ts";
import { EmojiPanel } from "./EmojiPanel.tsx";
import { placePopover } from "./popover.ts";

/**
 * A button that opens an emoji picker (Popover API: top layer, light dismiss, Esc): EmojiPanel.tsx. Picking one closes
 * it.
 */
export function EmojiPicker({
  label,
  onPick,
  class: cls,
  children,
}: {
  /** The button's name (for screen readers and its tooltip). */
  label: string;
  onPick: (u: string) => void;
  class?: string;
  /** What the button shows. */
  children: ComponentChildren;
}) {
  const id = useId();
  const pop = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  // Gone (its comment re-rendered elsewhere, say): never left open on its own.
  useEffect(() => {
    const el = pop.current;
    return () => {
      if (el?.matches(":popover-open")) {
        el.hidePopover();
      }
    };
  }, []);
  const [opens, setOpens] = useState(0);
  const onToggle = (e: Event) => {
    const el = pop.current;
    if ((e as ToggleEvent).newState === "open" && el && trigger.current) {
      placePopover(el, trigger.current);
      setOpens((n) => n + 1);
    }
  };
  return (
    <>
      <button
        ref={trigger}
        type="button"
        popovertarget={id}
        aria-haspopup="dialog"
        title={label}
        aria-label={label}
        class={cls}
      >
        {children}
      </button>
      <div
        ref={pop}
        id={id}
        popover="auto"
        role="dialog"
        aria-label={label}
        class="fixed m-0 rounded-lg border border-zinc-200 bg-white p-2 shadow-lg [&:popover-open]:flex"
        onToggle={onToggle}
      >
        <EmojiPanel
          opened={opens}
          onPick={(u) => {
            pop.current?.hidePopover();
            onPick(u);
          }}
        />
      </div>
    </>
  );
}
